import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCardText,
  CCol,
  CContainer,
  CImage,
  CRow,
  CAlert,
  CFormLabel,
  CFormTextarea,
  CBadge,
} from '@coreui/react'
import { cilSave, cilSend } from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import apiClient from '../../services/apiClient'
import { useNavigate } from 'react-router-dom'
import { buildApiUrl } from '../../utils/getConfiguration'
import InternalAppHeader from './components/InternalAppHeader'
import { useTranslation } from 'react-i18next'
import { SplashScreen } from '@capacitor/splash-screen'

const SuppliersInnoProjectDescription = () => {
  const { t } = useTranslation()
  const [ideaScopes, setIdeaScopes] = useState({})
  const [ideaTimelines, setIdeaTimelines] = useState({})
  const [ideaBudgets, setIdeaBudgets] = useState({})
  const [ideaResources, setIdeaResources] = useState({})
  const [ideaRisks, setIdeaRisks] = useState({})
  const [ideaVendors, setIdeaVendors] = useState({})
  const [startups, setStartups] = useState([])

  const fetchInnovationsData = async () => {
    try {
      const account_ID = localStorage.getItem('account_ID')
      const response = await apiClient.get(buildApiUrl('startupsList', { account_ID }))
      setStartups(response.data)

      // Inicializace hodnot pro každý startup
      const initialScopes = {}
      const initialTimelines = {}
      const initialBudgets = {}
      const initialResources = {}
      const initialRisks = {}
      const initialVendors = {}
      response.data.forEach((startup) => {
        initialScopes[startup.startup_id] = Array.isArray(startup.scope)
          ? startup.scope[0] || ''
          : startup.scope || ''
        initialTimelines[startup.startup_id] = Array.isArray(startup.timeline)
          ? startup.timeline[0] || ''
          : startup.timeline || ''
        initialBudgets[startup.startup_id] = Array.isArray(startup.budget)
          ? startup.budget[0] || ''
          : startup.budget || ''
        initialResources[startup.startup_id] = Array.isArray(startup.resources)
          ? startup.resources[0] || ''
          : startup.resources || ''
        initialRisks[startup.startup_id] = Array.isArray(startup.risks)
          ? startup.risks[0] || ''
          : startup.risks || ''
        initialVendors[startup.startup_id] = Array.isArray(startup.vendor)
          ? startup.vendor[0] || ''
          : startup.vendor || ''
      })
      setIdeaScopes(initialScopes)
      setIdeaTimelines(initialTimelines)
      setIdeaBudgets(initialBudgets)
      setIdeaResources(initialResources)
      setIdeaRisks(initialRisks)
      setIdeaVendors(initialVendors)
    } catch (error) {
      console.error('Error fetching startups', error)
    }
  }

  const hideSplashScreen = useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }

    const splash = document.getElementById('splash-screen')
    if (splash) {
      splash.style.display = 'none'
    }

    if (typeof SplashScreen?.hide === 'function') {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) => {
        console.warn('Unable to hide splash screen in SuppliersInnoProjectDescription', error)
      })
    } else if (typeof window.SplashScreen?.hide === 'function') {
      window.SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) => {
        console.warn('Unable to hide splash screen in SuppliersInnoProjectDescription', error)
      })
    }
  }, [])

  useEffect(() => {
    hideSplashScreen()
  }, [hideSplashScreen])

  useEffect(() => {
    fetchInnovationsData().finally(hideSplashScreen)
  }, [hideSplashScreen])

  let navigate = useNavigate()

  const handleClickSave = async (startup_ID, scope, timeline, budget, resources, risks, vendor) => {
    try {
      console.log('Account_ID ' + localStorage.getItem('account_ID'))
      //console.log('Reminder_ID ' + localStorage.getItem('taskid'))
      //console.log('Supplier_ID ' + localStorage.getItem('supplierid'))

      //const reminder_ID = localStorage.getItem('taskid')
      const account_ID = localStorage.getItem('account_ID')
      //const supplier_ID = localStorage.getItem('supplierid')
      //const supplier_name = name
      //const supplier_contact_name = contact
      //const supplier_contact_tel = tel
      await apiClient.get(
        buildApiUrl('startupIdeaSaveProject', {
          account_ID,
          startup_ID,
          scope,
          timeline,
          budget,
          resources,
          risks,
          vendor,
        }),
      )
      // Example: buildApiUrl('startupAssign', { account_ID: 3, startup_ID: 1, idea: 'Test' })
      fetchInnovationsData()
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const typeLabelMap = useMemo(
    () => ({
      F: t('suppliersInno.types.F'),
      C: t('suppliersInno.types.C'),
      J: t('suppliersInno.types.J'),
    }),
    [t],
  )

  function getIdeaTypeLabel(type) {
    return typeLabelMap[type] || ''
  }

  return (
    <div className="buying-list-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader
          title={t('suppliersInnoProjectDescription.headerTitle')}
          onBack={() => window.history.back()}
        />

        <CRow className="justify-content-center g-0 mt-2">
          <CCol lg={10}>
            <CCard className="border-0 shadow-sm">
              <CCardBody className="p-4 p-lg-5 d-flex flex-column gap-4">
                <div className="d-flex justify-content-end align-items-center">
                  <CButton
                    color="primary"
                    className="suppliers-inno__next-btn"
                    onClick={() => {
                      console.log('Send project description')
                      navigate('/')
                    }}
                    aria-label={t('suppliersInnoProjectDescription.actions.submit')}
                  >
                    <CIcon icon={cilSend} />
                  </CButton>
                </div>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <h4 className="mb-0">{t('suppliersInnoProjectDescription.sections.title')}</h4>
                  <CBadge color="primary">{startups.length}</CBadge>
                </div>
                {startups.length > 0 ? (
                  <CRow className="gy-4">
                    {startups.map((startup, index) => {
                      const ideaTypeLabel = getIdeaTypeLabel(startup.type)
                      const scopeValue = ideaScopes[startup.startup_id] || ''
                      const timelineValue = ideaTimelines[startup.startup_id] || ''
                      const budgetValue = ideaBudgets[startup.startup_id] || ''
                      const resourcesValue = ideaResources[startup.startup_id] || ''
                      const risksValue = ideaRisks[startup.startup_id] || ''
                      const vendorValue = ideaVendors[startup.startup_id] || ''
                      const rawDescription =
                        typeof startup.idea === 'string' && startup.idea.length > 0
                          ? startup.idea
                          : ''
                      const description = rawDescription.trim()
                      const hasDescription = description.length > 0
                      const linkTarget =
                        startup.link && /^https?:\/\//i.test(startup.link)
                          ? startup.link
                          : startup.link
                            ? `https://${startup.link}`
                            : null
                      const pictureSrc = startup.pic
                        ? `https://www.emerkur.eu/startups/${startup.pic}`
                        : null

                      return (
                        <CCol xs={12} key={startup.startup_id}>
                          <CCard className="h-100">
                            <CCardBody>
                              <div className="d-flex flex-column gap-4">
                                <div className="d-flex flex-wrap align-items-center gap-3">
                                  <CBadge color="primary" className="px-3 py-2 fs-6">
                                    #{index + 1}
                                  </CBadge>
                                  <strong className="text-medium-emphasis">
                                    {t('suppliersInnoProjectDescription.labels.ideaType')}
                                  </strong>
                                  {ideaTypeLabel ? (
                                    <CBadge color="info" className="text-uppercase">
                                      {ideaTypeLabel}
                                    </CBadge>
                                  ) : (
                                    <span className="text-medium-emphasis">
                                      {t('suppliersInnoProjectDescription.labels.typeUndefined')}
                                    </span>
                                  )}
                                </div>
                                <CRow className="gy-3 align-items-start">
                                  <CCol xs={12} md={4}>
                                    <div className="d-flex flex-column gap-3">
                                      {pictureSrc && (
                                        <CImage
                                          rounded
                                          orientation="top"
                                          className="border p-2 bg-white"
                                          src={pictureSrc}
                                          width={160}
                                          height={90}
                                          alt={t('suppliersInnoProjectDescription.labels.logoAlt')}
                                        />
                                      )}
                                      <CCardText className="mb-0">
                                        <strong>
                                          {t('suppliersInnoProjectDescription.labels.ideaTitle')}
                                        </strong>
                                        <br />
                                        {hasDescription ? (
                                          description
                                        ) : (
                                          <button
                                            type="button"
                                            className="innovation-description-missing"
                                            onClick={() => window.history.go(-2)}
                                          >
                                            {t(
                                              'suppliersInnoProjectDescription.labels.descriptionMissing',
                                            )}
                                          </button>
                                        )}
                                      </CCardText>
                                      {linkTarget && (
                                        <CCardText className="mb-0">
                                          <a
                                            href={linkTarget}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ wordBreak: 'break-all' }}
                                            onClick={(e) => {
                                              window.open(
                                                linkTarget,
                                                '_blank',
                                                'noopener,noreferrer',
                                              )
                                              e.preventDefault()
                                            }}
                                          >
                                            {startup.link}
                                          </a>
                                        </CCardText>
                                      )}
                                    </div>
                                  </CCol>
                                  <CCol xs={12} md={8}>
                                    <div className="d-flex flex-column gap-3">
                                      <div className="d-flex flex-column gap-2">
                                        <CFormLabel
                                          htmlFor={`ideaScopeDescription-${startup.startup_id}`}
                                          className="fw-semibold mb-0"
                                        >
                                          {t('suppliersInnoProjectDescription.labels.scope')}
                                        </CFormLabel>
                                        <CFormTextarea
                                          id={`ideaScopeDescription-${startup.startup_id}`}
                                          rows={3}
                                          placeholder={t(
                                            'suppliersInnoProjectDescription.placeholders.scope',
                                          )}
                                          aria-label={t(
                                            'suppliersInnoProjectDescription.labels.scope',
                                          )}
                                          value={scopeValue}
                                          onChange={(e) =>
                                            setIdeaScopes({
                                              ...ideaScopes,
                                              [startup.startup_id]: e.target.value,
                                            })
                                          }
                                        />
                                      </div>
                                      <div className="d-flex flex-column gap-2">
                                        <CFormLabel
                                          htmlFor={`ideaTimeline-${startup.startup_id}`}
                                          className="fw-semibold mb-0"
                                        >
                                          {t('suppliersInnoProjectDescription.labels.timeline')}
                                        </CFormLabel>
                                        <CFormTextarea
                                          id={`ideaTimeline-${startup.startup_id}`}
                                          rows={3}
                                          placeholder={t(
                                            'suppliersInnoProjectDescription.placeholders.timeline',
                                          )}
                                          aria-label={t(
                                            'suppliersInnoProjectDescription.labels.timeline',
                                          )}
                                          value={timelineValue}
                                          onChange={(e) =>
                                            setIdeaTimelines({
                                              ...ideaTimelines,
                                              [startup.startup_id]: e.target.value,
                                            })
                                          }
                                        />
                                      </div>
                                      <div className="d-flex flex-column gap-2">
                                        <CFormLabel
                                          htmlFor={`ideaBudget-${startup.startup_id}`}
                                          className="fw-semibold mb-0"
                                        >
                                          {t('suppliersInnoProjectDescription.labels.budget')}
                                        </CFormLabel>
                                        <CFormTextarea
                                          id={`ideaBudget-${startup.startup_id}`}
                                          rows={3}
                                          placeholder={t(
                                            'suppliersInnoProjectDescription.placeholders.budget',
                                          )}
                                          aria-label={t(
                                            'suppliersInnoProjectDescription.labels.budget',
                                          )}
                                          value={budgetValue}
                                          onChange={(e) =>
                                            setIdeaBudgets({
                                              ...ideaBudgets,
                                              [startup.startup_id]: e.target.value,
                                            })
                                          }
                                        />
                                      </div>
                                      <div className="d-flex flex-column gap-2">
                                        <CFormLabel
                                          htmlFor={`ideaResource-${startup.startup_id}`}
                                          className="fw-semibold mb-0"
                                        >
                                          {t('suppliersInnoProjectDescription.labels.resources')}
                                        </CFormLabel>
                                        <CFormTextarea
                                          id={`ideaResource-${startup.startup_id}`}
                                          rows={3}
                                          placeholder={t(
                                            'suppliersInnoProjectDescription.placeholders.resources',
                                          )}
                                          aria-label={t(
                                            'suppliersInnoProjectDescription.labels.resources',
                                          )}
                                          value={resourcesValue}
                                          onChange={(e) =>
                                            setIdeaResources({
                                              ...ideaResources,
                                              [startup.startup_id]: e.target.value,
                                            })
                                          }
                                        />
                                      </div>
                                      <div className="d-flex flex-column gap-2">
                                        <CFormLabel
                                          htmlFor={`ideaRisk-${startup.startup_id}`}
                                          className="fw-semibold mb-0"
                                        >
                                          {t('suppliersInnoProjectDescription.labels.risks')}
                                        </CFormLabel>
                                        <CFormTextarea
                                          id={`ideaRisk-${startup.startup_id}`}
                                          rows={3}
                                          placeholder={t(
                                            'suppliersInnoProjectDescription.placeholders.risks',
                                          )}
                                          aria-label={t(
                                            'suppliersInnoProjectDescription.labels.risks',
                                          )}
                                          value={risksValue}
                                          onChange={(e) =>
                                            setIdeaRisks({
                                              ...ideaRisks,
                                              [startup.startup_id]: e.target.value,
                                            })
                                          }
                                        />
                                      </div>
                                      <div className="d-flex flex-column gap-2">
                                        <CFormLabel
                                          htmlFor={`ideaVendor-${startup.startup_id}`}
                                          className="fw-semibold mb-0"
                                        >
                                          {t('suppliersInnoProjectDescription.labels.vendor')}
                                        </CFormLabel>
                                        <CFormTextarea
                                          id={`ideaVendor-${startup.startup_id}`}
                                          rows={3}
                                          placeholder={t(
                                            'suppliersInnoProjectDescription.placeholders.vendor',
                                          )}
                                          aria-label={t(
                                            'suppliersInnoProjectDescription.labels.vendor',
                                          )}
                                          value={vendorValue}
                                          onChange={(e) =>
                                            setIdeaVendors({
                                              ...ideaVendors,
                                              [startup.startup_id]: e.target.value,
                                            })
                                          }
                                        />
                                      </div>
                                      <div className="d-flex justify-content-end">
                                        <CButton
                                          color="success"
                                          title={t('suppliersInnoProjectDescription.actions.save')}
                                          aria-label={t(
                                            'suppliersInnoProjectDescription.actions.save',
                                          )}
                                          onClick={() => {
                                            console.log(
                                              'Save idea for startup Nr.' + startup.startup_id,
                                            )
                                            handleClickSave(
                                              startup.startup_id,
                                              scopeValue,
                                              timelineValue,
                                              budgetValue,
                                              resourcesValue,
                                              risksValue,
                                              vendorValue,
                                            )
                                          }}
                                        >
                                          <CIcon icon={cilSave} size="xxl" />
                                        </CButton>
                                      </div>
                                    </div>
                                  </CCol>
                                </CRow>
                              </div>
                            </CCardBody>
                          </CCard>
                        </CCol>
                      )
                    })}
                  </CRow>
                ) : (
                  <CAlert color="info" className="mb-0">
                    {t('suppliersInnoProjectDescription.messages.empty')}
                  </CAlert>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default SuppliersInnoProjectDescription
